import { Canvas, PencilBrush } from 'fabric';
import { BrushStyle } from './drawing-tool.model';

/**
 * Custom PencilBrush that forces full re-render for dashed lines.
 *
 * Fabric.js PencilBrush uses incremental rendering by default (only draws
 * the newest segment), which doesn't re-apply strokeDashArray before each stroke.
 * By overriding needsFullRender() to return true, we force the brush to
 * redraw the entire path on each mouse move, ensuring a consistent dash pattern.
 *
 * Without this fix, the dash pattern varies based on mouse speed:
 * - Slow movement = smaller gaps (more points captured)
 * - Fast movement = larger gaps (fewer points captured)
 *
 * ## Usage
 *
 * ```typescript
 * canvas.freeDrawingBrush = DashedPencilBrush.createForStyle(
 *     canvas, 'dashed', '#FFFFFF', 8
 * );
 * ```
 */
export class DashedPencilBrush extends PencilBrush {
    /**
     * Complete style definitions for dashed brushes.
     * Dash patterns scale proportionally with stroke width:
     * - dashMultiplier × width = dash length
     * - gapMultiplier × width = gap length
     */
    static readonly STYLES = {
        dashed: {
            dashMultiplier: 2,
            gapMultiplier: 3,
            lineCap: 'round' as CanvasLineCap,
        },
    } as const;

    /** Check if a brush style requires DashedPencilBrush. */
    static shouldUseDashedBrush(style: BrushStyle): boolean {
        return style in DashedPencilBrush.STYLES;
    }

    /**
     * Get dash array for a brush style, scaled by stroke width.
     * @returns `[dashLength, gapLength]` scaled by width, or `undefined` for the
     *          solid style or an invalid width (0, negative, NaN, Infinity).
     */
    static getDashArray(style: BrushStyle, width: number): [number, number] | undefined {
        // Validate width: must be a finite positive number
        if (!Number.isFinite(width) || width <= 0) {
            return undefined;
        }

        if (style in DashedPencilBrush.STYLES) {
            const { dashMultiplier, gapMultiplier } =
                DashedPencilBrush.STYLES[style as keyof typeof DashedPencilBrush.STYLES];
            return [dashMultiplier * width, gapMultiplier * width];
        }
        return undefined;
    }

    /** Get line cap style for a brush style ('round' for dashed, undefined for solid). */
    static getLineCap(style: BrushStyle): CanvasLineCap | undefined {
        if (style in DashedPencilBrush.STYLES) {
            return DashedPencilBrush.STYLES[style as keyof typeof DashedPencilBrush.STYLES].lineCap;
        }
        return undefined;
    }

    /**
     * Factory: create a brush configured for any brush style. Returns a
     * DashedPencilBrush for dashed (needs full re-render for a consistent preview),
     * or a regular PencilBrush for solid.
     */
    static createForStyle(
        canvas: Canvas,
        style: BrushStyle,
        color: string,
        width: number
    ): PencilBrush {
        const isDashed = DashedPencilBrush.shouldUseDashedBrush(style);
        const brush = isDashed
            ? new DashedPencilBrush(canvas)
            : new PencilBrush(canvas);

        brush.color = color;
        brush.width = width;
        brush.strokeDashArray = DashedPencilBrush.getDashArray(style, width) ?? null;
        brush.strokeLineCap = DashedPencilBrush.getLineCap(style) ?? 'round';

        return brush;
    }

    /**
     * Force a full render when a dash pattern is set, so the dash is applied
     * consistently during the live preview (the parent only forces it for
     * transparent colors, shadows, or straight-line mode).
     */
    override needsFullRender(): boolean {
        if (this.strokeDashArray?.length) {
            return true;
        }
        return super.needsFullRender();
    }
}
